import React, { useState } from "react";
import * as ExcelJS from "exceljs";
import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { saveAs } from "file-saver";
import getParticipantesFinalesArt from '../../services/participant/participantesFinalesArtService';
import { getArbolDirectorios } from '../../services/dashboardInicial/getArbolDirectorios';

import logoART from "../../assets/logo_participantesF/ART.png";

const COLORS = {
  // 🔵 AZUL (se mantiene)
  AZUL_OSCURO: "FF0B2C5D",
  AZUL_CLARO: "FFD9E3F0",

  // 🔷 AZUL MARINO
  AZUL_MARINO_OSCURO: "FF082444",
  AZUL_MARINO_CLARO: "FFE8EFF8",

  // 🟢 VERDE INSTITUCIONAL
  VERDE_OSCURO: "FF1F6B3A",
  VERDE_CLARO: "FFE3F2E5",

  // 🟤 MARRÓN
  MARRON_OSCURO: "FF7A4A2E",
  MARRON_CLARO: "FFF2E2D5",

  // ☕ CAFÉ
  CAFE_OSCURO: "FF5C3A1E",
  CAFE_CLARO: "FFEDE3D8",

  // 🟣 MORADO
  MORADO_OSCURO: "FF4A235A",
  MORADO_CLARO: "FFE8DAEF",

  // 🔴 VINOTINTO
  VINOTINTO_OSCURO: "FF641E16",
  VINOTINTO_CLARO: "FFF5DAD8",

  // ⚫ GRIS
  GRIS_OSCURO: "FF4D4D4D",
  GRIS_CLARO: "FFEAEAEA",

  // 🟠 NARANJA TIERRA
  NARANJA_OSCURO: "FFAF601A",
  NARANJA_CLARO: "FFFBE5D6",

  // 🟡 MOSTAZA
  MOSTAZA_OSCURO: "FF9A7D0A",
  MOSTAZA_CLARO: "FFFFF2CC",

  // ⚪ BLANCO (base)
  BLANCO: "FFFFFFFF",
};


const imageToBase64 = async (imagePath) => {
  const response = await fetch(imagePath);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () =>
      resolve(reader.result.replace(/^data:image\/(png|jpg|jpeg);base64,/, ""));
    reader.readAsDataURL(blob);
  });
};
const withSoftBorder = (cell) => {
  cell.border = {
    top: { style: "thin", color: { argb: COLORS.BLANCO } },
    left: { style: "thin", color: { argb: COLORS.BLANCO } },
    right: { style: "thin", color: { argb: COLORS.BLANCO } },
    bottom: { style: "thin", color: { argb: COLORS.BLANCO } },
  };
};

const applyBorderToRange = (
  sheet,
  startRow,
  endRow,
  startCol,
  endCol,
  style = "thin",
  color = COLORS.AZUL_OSCURO
) => {
  for (let row = startRow; row <= endRow; row++) {
    for (let col = startCol; col <= endCol; col++) {
      const cell = sheet.getCell(row, col);
      cell.border = {
        top: { style, color: { argb: color } },
        left: { style, color: { argb: color } },
        right: { style, color: { argb: color } },
        bottom: { style, color: { argb: color } },
      };
    }
  }
};


const createHeader = (sheet, range, text, bgColor) => {
  sheet.mergeCells(range);
  const cell = sheet.getCell(range.split(":")[0]);
  cell.value = text.toUpperCase();
  cell.font = { size: 10, color: { argb: COLORS.BLANCO } };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
  withSoftBorder(cell);
};
const createSubHeader = (sheet, cellAddr, text, bgColor) => {
  const cell = sheet.getCell(cellAddr);
  cell.value = text ?? "";
  cell.font = { size: 10, color: { argb: COLORS.AZUL_OSCURO } };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
  withSoftBorder(cell);
};

const transformarRutaARutaLista = (ruta) =>
  ruta.split('/').filter(segment => segment);

const contieneArchivo = (dataDirectorios, nombres) => {
  let currentNode = dataDirectorios.find(node => node.name === nombres[0]);

  for (let i = 1; i < nombres.length; i++) {
    if (!currentNode || !currentNode.children) return false;
    currentNode = currentNode.children.find(child => child.name === nombres[i]);
  }

  if (currentNode && currentNode.children?.length > 0) {
    return currentNode.children.some(child => child.type === "file");
  }

  return false;
};




const ExportExcelBodecom = ({ proyectoCodigo }) => {
  const [loading, setLoading] = useState(false);

  const exportExcel = async () => {
    try {
      setLoading(true);
      const response = await getParticipantesFinalesArt(proyectoCodigo);
      if (response.status !== 200 || !response.data?.length) {
        message.warning("No hay participantes para exportar");
        return;
      }
      const participantes = response.data;

      const { data: dataDirectorios } = await getArbolDirectorios();
      const participantesValidados = participantes.map((p) => {
        const rutas = [
          {
            ruta: `${p.codigo_proyecto}/Participantes/${p.numero_identificacion}/Cédula`,
            campo: 'validado_registraduria',
          },
          {
            ruta: `${p.codigo_proyecto}/Participantes/${p.numero_identificacion}/Antecedentes Procuraduría`,
            campo: 'validado_procuraduria',
          },
          {
            ruta: `${p.codigo_proyecto}/Participantes/${p.numero_identificacion}/Antecedentes Policía`,
            campo: 'validado_policia',
          },
        ];

        const updated = { ...p };

        rutas.forEach(({ ruta, campo }) => {
          const existe = contieneArchivo(
            dataDirectorios,
            transformarRutaARutaLista(ruta)
          );

          updated[campo] = existe ? 'SI' : 'NO';
        });

        return updated;
      });

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("FORMATO ART", {
        views: [{ showGridLines: false, zoomScale: 30 }],
      });

      for (let col = 2; col <= 81; col++) {
        sheet.getColumn(col).width = 25;
      }



      // 🔹 TÍTULO SUPERIOR
      sheet.mergeCells("F1:AE1");
      const tituloPrincipal = sheet.getCell("F1");
      tituloPrincipal.value = "CARACTERIZACIÓN POBLACIÓN PARTICIPANTE";
      tituloPrincipal.font = { bold: true, size: 15, color: { argb: COLORS.AZUL_OSCURO } };
      tituloPrincipal.alignment = { vertical: "middle", horizontal: "center" };

      // 🔹 TÍTULO SECUNDARIO (BAJA UNA FILA)
      sheet.mergeCells("F2:AE2");
      const tituloSecundario = sheet.getCell("F2");
      tituloSecundario.value = "ESTRUCTURACIÓN Y EJECUCIÓN DE PROYECTOS";
      tituloSecundario.font = { bold: true, size: 15, color: { argb: COLORS.AZUL_OSCURO } };
      tituloSecundario.alignment = { vertical: "middle", horizontal: "center" };


      sheet.mergeCells("A1:E3");

      sheet.getRow(1).height = 35;
      sheet.getRow(2).height = 35;
      sheet.getRow(3).height = 35;

      for (let row = 1; row <= 2; row++) {
        for (let col = 1; col <= 31; col++) {
          const cell = sheet.getCell(row, col);
          cell.border = {
            top: { style: "medium", color: { argb: COLORS.AZUL_OSCURO } },
            left: { style: "medium", color: { argb: COLORS.AZUL_OSCURO } },
            right: { style: "medium", color: { argb: COLORS.AZUL_OSCURO } },
            bottom: { style: "medium", color: { argb: COLORS.AZUL_OSCURO } },
          };
        }
      }
      // 🟦 BORDE ÚNICO PARA LA ESTRUCTURA INSTITUCIONAL (A3 → AE3)
      applyBorderToRange(sheet, 3, 3, 1, 31, "medium", COLORS.AZUL_OSCURO);


      ["A1", "F1", "A2", "F2", "P2", "U2", "X2"].forEach((cellAddr) => {
        sheet.getCell(cellAddr).alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
      });

      const logoBase64 = await imageToBase64(logoART);
      const imageId = workbook.addImage({
        base64: logoBase64,
        extension: "png",
      });

      sheet.addImage(imageId, {
        tl: { col: 0.3, row: 0.6 },   // mueve un poco arriba y a la izquierda
        br: { col: 5.0, row: 2.3 },   // más ancho y más alto
      });


      sheet.getCell("A1").alignment = {
        vertical: "middle",
        horizontal: "center",
      };


      sheet.mergeCells("F3:O3");
      sheet.getCell("F3").value =
        "DIRECCIÓN DE ESTRUCTURACIÓN Y EJECUCIÓN DE PROYECTOS/SUBDIRECCIÓN DE DESARROLLO ECONÓMICO";

      sheet.mergeCells("P3:T3");
      sheet.getCell("P3").value = "Código: FM-EEP-60";

      sheet.mergeCells("U3:W3");
      sheet.getCell("U3").value = "Versión: 01";

      sheet.mergeCells("X3:AE3");
      sheet.getCell("X3").value = "Fecha de publicación: 17-04-2023";

      ["F3", "P3", "U3", "X3"].forEach((c) => {
        sheet.getCell(c).font = { size: 14, color: { argb: COLORS.AZUL_OSCURO } };
        sheet.getCell(c).alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
      });

      sheet.getRow(4).height = 25;
      sheet.getRow(5).height = 25;
      sheet.getRow(6).height = 25;

      createHeader(sheet, "A5:A6", "No.", COLORS.AZUL_OSCURO);
      createHeader(sheet, "B5:L5", "DATOS BÁSICOS", COLORS.AZUL_OSCURO);
      createHeader(sheet, "M5:Q5", "NIVEL ESCOLARIDAD (Marque X)", COLORS.VERDE_OSCURO);
      createHeader(sheet, "R5:AD5", "POBLACIÓN (Marque X)", COLORS.MARRON_OSCURO);
      createHeader(sheet, "AE5:AL5", "INFORMACIÓN FAMILIAR", COLORS.CAFE_OSCURO);
      createHeader(sheet, "AM5:BF5", "INFORMACIÓN DEL PROYECTO VINCULADO", COLORS.MORADO_OSCURO);
      createHeader(sheet, "BG5:BJ5", "DATOS DE CONTACTO", COLORS.VINOTINTO_OSCURO);
      createHeader(sheet, "BK5:BL5", "CONECTIVIDAD", COLORS.GRIS_OSCURO);
      createHeader(sheet, "BM5:BT5", "BANCARIZACIÓN", COLORS.NARANJA_OSCURO);
      createHeader(sheet, "BU5:BX5", "INFORMACIÓN ORGANIZACIONAL", COLORS.MOSTAZA_OSCURO);
      createHeader(sheet, "BY5:CC5", "VALIDACIÓN INTERNA ART", COLORS.AZUL_MARINO_OSCURO);

      [
        "Primer Nombre",
        "Segundo Nombre",
        "Primer Apellido",
        "Segundo Apellido",
        "Tipo de Identificación",
        "No. de Identificación",
        "Sexo",
        "Fecha de Nacimiento",
        "¿Sabe Leer y Escribir? Si/No",
        "Ingresos Mensuales (Pesos M/Cte)",
        "Línea Económica Principal",
      ].forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(6, 2 + i).address, t, COLORS.AZUL_CLARO)
      );

      ["Ninguna", "Primaria", "Secundaria", "Técnica", "Profesional"]
        .forEach((t, i) =>
          createSubHeader(sheet, sheet.getCell(6, 13 + i).address, t, COLORS.VERDE_CLARO)
        );

      [
        "Campesino", "Comunidad Indígena", "Comunidad NARP", "Comunidad ROM",
        "Víctima", "Discapacidad", "LGBT+", "Mujer Rural", "Joven Rural",
        "Adulto Mayor", "Reincorporado", "Otro", "¿Cuál?",
      ].forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(6, 18 + i).address, t, COLORS.MARRON_CLARO)
      );

      [
        "Es Cabeza de Familias (SI y NO)", "No. Personas a Cargo",
        "Total Núcleo Familiar", "Estado Civil",
        "Nombre Completo Cónyuge", "Tipo de Identificación",
        "No. Identificación", "Número de Contacto",
      ].forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(6, 31 + i).address, t, COLORS.CAFE_CLARO)
      );

      [
        "Código Proyecto ART", "Nombre del proyecto asociado", "Subregión PDET", "Departamento", "Municipio",
        "Tipo Zona de Influencia Directa", "Nombre de Zona de influencia directa", "Código Shape Veredas ART",
        "Nombre del Predio/Finca", "Relación de Tenencia con el Predio", "Georeferenciación Latitud", "Georeferenciación Longitud",
        "No. Hectareas Destinadas al Proyecto", "Línea Productiva 1 Proyecto", "Línea Productiva 2 Proyecto",
        "Distancia Lugar del Proyecto a Cabecera Municipal - (Kilómetros)", "Tiempo Lugar del Proyecto a Cabecera Muninicpal - (Minutos)",
        "Medio de Transporte", "¿Vive en el mismo lugar del Proyecto? SI/No", "¿Lugar de Vivienda?"
      ].forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(6, 39 + i).address, t, COLORS.MORADO_CLARO)
      );

      ["Dirección de Correspondencia", "Número Teléfono de Contacto 1", "Número Teléfono de Contacto 2", "Correo Electrónico"]
        .forEach((t, i) =>
          createSubHeader(sheet, sheet.getCell(6, 59 + i).address, t, COLORS.VINOTINTO_CLARO)
        );

      ["Telefónica Si/No", "Internet Si/No"]
        .forEach((t, i) =>
          createSubHeader(sheet, sheet.getCell(6, 63 + i).address, t, COLORS.GRIS_CLARO)
        );

      [
        "Tipo Establecimiento Bancario cercano a su Vivienda", "¿Cuenta con Productos Financieros? Si/No",
        "Cuenta de Ahorro Marque X", "Tarjeta de Crédito Marque X", "Otros Créditos Marque X", "Tipo de Entidad",
        "¿Ha recibido Capacitación en Educación Financiera? Si/No", "¿Interesado en  Información de Inclusión Financiera? Si/No"
      ].forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(6, 65 + i).address, t, COLORS.NARANJA_CLARO)
      );

      [
        "¿Vinculado a algúna Organización? Si/No", "Tipo de Organización",
        "Nombre de la Organización", "Es una organización de Mujeres? Si/No",
      ].forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(6, 73 + i).address, t, COLORS.MOSTAZA_CLARO)
      );

      [
        "¿Validado Por Registraduría? Si/No", "¿Validado Por Procuraduría? Si/No", "¿Validado Por Policía? Si/No",
        "Estado dentro del proyecto (Activo-Suspendido-Retirado-Con Novedad)", "Observación Estado",
      ].forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(6, 77 + i).address, t, COLORS.AZUL_MARINO_CLARO)
      );


      let startRow = 7;
      participantesValidados.forEach((p, index) => {
        const row = sheet.getRow(startRow + index);

        // =====================
        // DATOS BÁSICOS (A–L)
        // =====================
        row.getCell(1).value = p.no;
        row.getCell(2).value = p.nombre_1;
        row.getCell(3).value = p.nombre_2;
        row.getCell(4).value = p.apellido_1;
        row.getCell(5).value = p.apellido_2;
        row.getCell(6).value = p.tipo_identificacion;
        row.getCell(7).value = p.numero_identificacion;
        row.getCell(8).value = p.sexo;
        row.getCell(9).value = p.fecha_nacimiento;
        row.getCell(10).value = p.sabe_leer_escribir;


        const ingresosCell = row.getCell(11);

        const ingresoNumero =
          p.ingresos_mensuales !== null && p.ingresos_mensuales !== undefined
            ? Number(
              String(p.ingresos_mensuales)
                .replace(/\./g, '')
                .replace(/,/g, '')
            )
            : null;

        ingresosCell.value = ingresoNumero;
        ingresosCell.numFmt = '"$"#,##0.00'; // 💰 $600.000,00
        ingresosCell.alignment = {
          vertical: "middle",
          horizontal: "left",
        };



        row.getCell(12).value = p.linea_economica_principal;

        // =====================
        // NIVEL ESCOLARIDAD (M–Q)
        // =====================
        row.getCell(13).value = p.ninguna;
        row.getCell(14).value = p.primaria;
        row.getCell(15).value = p.secundaria;
        row.getCell(16).value = p.tecnica;
        row.getCell(17).value = p.profesional;

        // =====================
        // POBLACIÓN (R–AD)
        // =====================
        row.getCell(18).value = p.campesino;
        row.getCell(19).value = p.comunidad_indigena;
        row.getCell(20).value = p.comunidad_narp;
        row.getCell(21).value = p.comunidad_rom;
        row.getCell(22).value = p.victima;
        row.getCell(23).value = p.discapacidad;
        row.getCell(24).value = p.lgbt;
        row.getCell(25).value = p.mujer_rural;
        row.getCell(26).value = p.joven_rural;
        row.getCell(27).value = p.adulto_mayor;
        row.getCell(28).value = p.reincorporado;
        row.getCell(29).value = p.otro;
        row.getCell(30).value = p.otro_cual; // 👈 FALTANTE CLAVE

        // =====================
        // INFORMACIÓN FAMILIAR (AE–AL)
        // =====================
        row.getCell(31).value = p.cabeza_familia;
        row.getCell(32).value = p.num_personas_cargo;
        row.getCell(33).value = p.total_nucleo_familiar;
        row.getCell(34).value = p.estado_civil;
        row.getCell(35).value = p.nombre_conyuge;
        row.getCell(36).value = p.tipo_id_conyuge;
        row.getCell(37).value = p.identificacion_conyuge;
        row.getCell(38).value = p.numero_contacto_conyuge;

        // =====================
        // INFORMACIÓN PROYECTO (AM–BF)
        // =====================
        row.getCell(39).value = p.codigo_proyecto;
        row.getCell(40).value = p.nombre_proyecto;
        row.getCell(41).value = p.subregion_pdet;
        row.getCell(42).value = p.departamento;
        row.getCell(43).value = p.municipio;
        row.getCell(44).value = p.tipo_zona_influencia;
        row.getCell(45).value = p.nombre_zona_influencia;
        row.getCell(46).value = p.codigo_shape_vereda;
        row.getCell(47).value = p.nombre_predio;
        row.getCell(48).value = p.relacion_tenencia_predio;
        row.getCell(49).value = p.latitud;
        row.getCell(50).value = p.longitud;
        row.getCell(51).value = p.hectareas_proyecto;
        row.getCell(52).value = p.linea_productiva_1;
        row.getCell(53).value = p.linea_productiva_2;
        row.getCell(54).value = p.distancia_cabecera_km;
        row.getCell(55).value = p.tiempo_cabecera_min;
        row.getCell(56).value = p.medio_transporte;
        row.getCell(57).value = p.vive_en_proyecto;
        row.getCell(58).value = p.lugar_vivienda;

        // =====================
        // DATOS DE CONTACTO (BG–BJ)
        // =====================
        row.getCell(59).value = p.direccion_correspondencia;
        row.getCell(60).value = p.telefono_1;
        row.getCell(61).value = p.telefono_2;
        row.getCell(62).value = p.correo;

        // =====================
        // CONECTIVIDAD (BK–BL)
        // =====================
        row.getCell(63).value = p.telefonica;
        row.getCell(64).value = p.internet;

        // =====================
        // BANCARIZACIÓN (BM–BT)
        // =====================
        row.getCell(65).value = p.tipo_establecimiento_bancario;
        row.getCell(66).value = p.productos_financieros;
        row.getCell(67).value = p.cuenta_ahorro;
        row.getCell(68).value = p.tarjeta_credito;
        row.getCell(69).value = p.otros_creditos;
        row.getCell(70).value = p.tipo_entidad;
        row.getCell(71).value = p.capacitacion_financiera;
        row.getCell(72).value = p.interes_inclusion_financiera;

        // =====================
        // INFORMACIÓN ORGANIZACIONAL (BU–BX)
        // =====================
        row.getCell(73).value = p.vinculado_organizacion;
        row.getCell(74).value = p.tipo_organizacion;
        row.getCell(75).value = p.nombre_organizacion;
        row.getCell(76).value = p.organizacion_mujeres;

        // =====================
        // VALIDACIÓN ART (BY–CC)
        // =====================
        row.getCell(77).value = p.validado_registraduria;
        row.getCell(78).value = p.validado_procuraduria;
        row.getCell(79).value = p.validado_policia;
        row.getCell(80).value = p.estado_proyecto;
        const obsCell = row.getCell(81);
        obsCell.value = p.observacion_estado;
        obsCell.alignment = {
          vertical: "middle",
          horizontal: "left",
          wrapText: true,
        };

        // Bordes suaves
        row.eachCell((cell) => withSoftBorder(cell));
      });

      sheet.getColumn(81).width = 45;



      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "Formato_ART_Completo.xlsx");
      message.success("Archivo generado correctamente");
    } catch (error) {
      console.error(error);
      message.error("Error al generar el archivo Excel");
    } finally {
      setLoading(false);
    }
  };


  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      loading={loading}
      onClick={exportExcel}
      disabled={loading}
    >
      {loading ? "Generando archivo..." : "Exportar Participantes Finales"}
    </Button>

  );
};

export default ExportExcelBodecom;
